import * as React from 'react';
import { cn } from '@/lib/utils';

export interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  onClose?: () => void;
  /** Show correlationId on error (per Global UI Notification Standard) */
  correlationId?: string;
}

/**
 * Material Design toast notification
 * 
 * Per Global UI & Layout Enforcement - Notification/Toast Standard:
 * - Position: top-right
 * - Auto dismiss: 3-5 seconds
 * - Show API message
 * - Show correlationId on error
 */
export const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 4000, // 3-5 seconds per standard
  onClose,
  correlationId,
}) => {
  React.useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose?.();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const typeStyles = {
    success: 'bg-success text-primary-foreground',
    error: 'bg-destructive text-primary-foreground',
    info: 'bg-sky-600 text-card',
    warning: 'bg-warning-soft0 text-card',
  };

  const icons = {
    success: '✓',
    error: '✕',
    info: 'ℹ',
    warning: '⚠',
  };

  return (
    <div
      className={cn(
        // Individual toast — no fixed positioning; container handles placement
        'flex flex-col gap-1 px-4 py-3 rounded-lg shadow-lg animate-in slide-in-from-top-2 max-w-sm w-full pointer-events-auto',
        typeStyles[type]
      )}
      role="alert"
    >
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold">{icons[type]}</span>
        <span className="ui-input-text font-medium">{message}</span>
        {onClose && (
          <button
            onClick={onClose}
            className="ml-2 text-card hover:opacity-80 transition-opacity"
            aria-label="Close"
          >
            ✕
          </button>
        )}
      </div>
      {/* Show correlationId on error per Global UI Notification Standard */}
      {type === 'error' && correlationId && (
        <div className="ui-metadata text-card/80 pl-7">
          ID: {correlationId}
        </div>
      )}
    </div>
  );
};

// Toast Container and Context
export interface ToastMessage {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  correlationId?: string;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastMessage['type'], duration?: number, correlationId?: string) => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(undefined);

const ERROR_DEDUP_WINDOW_MS = 2000;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = React.useState<ToastMessage[]>([]);
  const lastErrorRef = React.useRef<{ message: string; timestamp: number }>({ message: '', timestamp: 0 });

  const showToast = React.useCallback(
    (message: string, type: ToastMessage['type'] = 'info', duration = 4000, correlationId?: string) => {
      // Dedup: suppress identical error messages arriving within a short window
      if (type === 'error') {
        const now = Date.now();
        if (
          message === lastErrorRef.current.message &&
          now - lastErrorRef.current.timestamp < ERROR_DEDUP_WINDOW_MS
        ) {
          return; // duplicate – skip
        }
        lastErrorRef.current = { message, timestamp: now };
      }

      const id = Math.random().toString(36).substring(7);
      setToasts((prev) => [...prev, { id, message, type, duration, correlationId }]);
    },
    []
  );

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/* Toast container — fixed top-right, above header (z-1000) and sidebar (z-1100) */}
      <div
        className="fixed top-5 right-5 flex flex-col gap-2 pointer-events-none"
        style={{ zIndex: 99999 }}
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            correlationId={toast.correlationId}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};


